import { useMemo, useState, useEffect } from 'react';
import { CalendarEvent } from '../../types/calendar';
import { getUniqueSourcesFromEvents, SourceInfo } from '../../utils/sourceInfo';
import { supabase } from '../../lib/supabase';

interface SourceBadgesProps {
  events: CalendarEvent[];
  calendarId?: string;
  color?: string;
}

function SourceFavicon({ src, label }: { src: string; label: string }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span className="w-3.5 h-3.5 rounded-sm bg-white/30 flex items-center justify-center text-[8px] font-bold leading-none">
        {label.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={label}
      className="w-3.5 h-3.5 rounded-sm object-contain"
      onError={() => setFailed(true)}
    />
  );
}

export default function SourceBadges({ events, calendarId, color = 'currentColor' }: SourceBadgesProps) {
  const [localEvents, setLocalEvents] = useState<CalendarEvent[]>([]);

  useEffect(() => {
    if (events.length > 0 || !calendarId) return;

    supabase
      .from('calendar_events')
      .select('id, title, start_datetime, end_datetime, source')
      .eq('calendar_id', calendarId)
      .in('sync_status', ['synced', 'pending_google_sync'])
      .not('source', 'is', null)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setLocalEvents(data.map(e => ({
            id: e.id,
            title: e.title,
            start: new Date(e.start_datetime),
            end: new Date(e.end_datetime),
            source: e.source || undefined,
          })));
        }
      });
  }, [events.length, calendarId]);

  const allEvents = events.length > 0 ? events : localEvents;

  const sources: SourceInfo[] = useMemo(
    () => getUniqueSourcesFromEvents(allEvents),
    [allEvents]
  );

  if (sources.length === 0) return null;

  return (
    <div className="flex items-center justify-center flex-wrap gap-2 mt-2">
      <span
        className="text-xs opacity-60 font-medium"
        style={{ color }}
      >
        Src:
      </span>
      {sources.map(source => (
        <span
          key={source.id}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 backdrop-blur-sm"
          style={{ color }}
        >
          <SourceFavicon src={source.faviconUrl} label={source.label} />
          {source.label}
        </span>
      ))}
    </div>
  );
}
